import { SendersDetailsFilterModel } from "./SendersDetailsFilterModel";

export const SENDERS_FILTER_KEY = "monitorDashboard.sendersFilter";

export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export function getTodayStr(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return yyyy + "-" + mm + "-" + dd;
}

export function loadSendersFilterFromStorage(target: SendersDetailsFilterModel): void {
    try {
        const raw = sessionStorage.getItem(SENDERS_FILTER_KEY);
        if (!raw) {
            return;
        }
        const parsed = JSON.parse(raw);
        if (parsed && parsed.dateFrom) {
            target.dateFrom = parsed.dateFrom;
        }
        if (parsed && parsed.dateTo) {
            target.dateTo = parsed.dateTo;
        }
        if (parsed && parsed.tenantId) {
            target.tenantId = parsed.tenantId;
        } else if (parsed && parsed.tenant) {
            target.tenantId = parsed.tenant;
        }
        if (parsed && parsed.providerId) {
            target.providerId = parsed.providerId;
        }
        if (parsed && parsed.customerType != null) {
            target.customerType = Number(parsed.customerType);
        }
    } catch (e) {
    }
}

export function buildSendersScopePayload(filter: SendersDetailsFilterModel): any {
    return {
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo || getTodayStr(),
        tenantId: filter.tenantId,
        providerId: filter.providerId,
        customerType: filter.customerType != null ? filter.customerType : 0,
        requestType: 0,
    };
}

export function buildActiveSendersScopePayload(filter: SendersDetailsFilterModel): any {
    const payload: any = buildSendersScopePayload(filter);
    if (filter.activeNow) {
        payload.activeNow = true;
    }
    return payload;
}

export function buildSendersExpirationScopePayload(
    filter: SendersDetailsFilterModel,
    chartYear: number | null,
    chartMonth: number | null,
): any {
    const payload: any = buildSendersScopePayload(filter);
    if (chartYear != null && chartMonth != null) {
        payload.chartYear = chartYear;
        payload.chartMonth = chartMonth;
    }
    return payload;
}

export function buildSendersRenewalScopePayload(
    filter: SendersDetailsFilterModel,
    chartYear: number | null,
    chartMonth: number | null,
): any {
    const payload: any = buildSendersScopePayload(filter);
    if (chartYear != null && chartMonth != null) {
        payload.chartYear = chartYear;
        payload.chartMonth = chartMonth;
    }
    return payload;
}

export function buildSendersDurationScopePayload(
    filter: SendersDetailsFilterModel,
    durationBucket: number | null,
): any {
    const payload: any = buildSendersScopePayload(filter);
    if (durationBucket != null) {
        payload.senderNameDuration = durationBucket;
    }
    return payload;
}

export function buildSendersByNodesScopePayload(
    filter: SendersDetailsFilterModel,
    chartTenantId: string,
): any {
    const payload: any = buildSendersScopePayload(filter);
    if (chartTenantId && chartTenantId !== EMPTY_GUID) {
        payload.chartTenantId = chartTenantId;
    }
    return payload;
}

export function buildSenderNameLengthComplianceScopePayload(
    filter: SendersDetailsFilterModel,
    nameLengthBucket: number | null,
): any {
    const payload: any = buildSendersScopePayload(filter);
    if (nameLengthBucket != null) {
        payload.senderNameLength = nameLengthBucket;
    }
    return payload;
}

export function assignSenderNameLengthComplianceRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.createdDate) +
            "-" +
            String(row.senderName) +
            "-" +
            String(row.senderNameLength) +
            "-" +
            String(row.serviceProvider) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function assignSendersByNodesRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.requestId) +
            "-" +
            String(row.createdDate) +
            "-" +
            String(row.senderName) +
            "-" +
            String(row.serviceProvider) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function formatSenderNameDuration(value: number, yearLabel: string, yearsLabel: string): string {
    return formatRenewalDuration(value, yearLabel, yearsLabel);
}

export function formatRenewalDuration(value: number, yearLabel: string, yearsLabel: string): string {
    const duration = value != null ? Number(value) : 0;
    if (!duration || isNaN(duration)) {
        return "";
    }
    if (duration >= 5) {
        return "5+ " + yearsLabel;
    }
    if (duration === 1) {
        return "1 " + yearLabel;
    }
    return String(duration) + " " + yearsLabel;
}

export function assignSendersDurationRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.requestId) +
            "-" +
            String(row.createdDate) +
            "-" +
            String(row.senderName) +
            "-" +
            String(row.senderNameDuration) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function assignSenderRenewalTimelineRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.requestId) +
            "-" +
            String(row.createdDate) +
            "-" +
            String(row.senderName) +
            "-" +
            String(row.renewalDuration) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function assignActiveSendersTimelineRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.requestId) +
            "-" +
            String(row.createdDate) +
            "-" +
            String(row.senderName) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function formatSenderExpiryDate(value: string): string {
    return formatCreatedDate(value);
}

export function createEmptyDateRangeInternal(): { start: string; end: string } {
    return {
        start: "",
        end: "",
    };
}

export function formatDateRangeText(rangeInternal: { start: string; end: string }): string {
    const from = rangeInternal && rangeInternal.start ? rangeInternal.start : "";
    const to = rangeInternal && rangeInternal.end ? rangeInternal.end : "";

    if (from && to) {
        return from + " - " + to;
    }
    if (from) {
        return from;
    }
    return "";
}

export function isDateRangeApplied(rangeInternal: { start: string; end: string }): boolean {
    const from = rangeInternal && rangeInternal.start ? rangeInternal.start : "";
    const to = rangeInternal && rangeInternal.end ? rangeInternal.end : "";
    return !!(from || to);
}

export function appendSenderExpiryDateRangeToPayload(
    payload: any,
    rangeInternal: { start: string; end: string },
    applied: boolean,
): void {
    if (!applied) {
        return;
    }

    const from = rangeInternal && rangeInternal.start ? rangeInternal.start : "";
    const to = rangeInternal && rangeInternal.end ? rangeInternal.end : "";

    if (from) {
        payload.senderExpiryDateFrom = from;
    }
    if (to) {
        payload.senderExpiryDateTo = to;
    }
}

export function formatCreatedDate(value: string): string {
    if (!value) {
        return "";
    }
    const s = String(value);
    if (s.length >= 10) {
        return s.substring(0, 10);
    }
    return s;
}

export function assignRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.createdDate) +
            "-" +
            String(row.requestId) +
            "-" +
            String(row.senderName) +
            "-" +
            String(row.senderType) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function downloadExcelFromBase64(base64: string, fileName: string): void {
    try {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (e) {
    }
}
